/* eslint-disable react-refresh/only-export-components */
import { MapPin, Navigation, X } from "lucide-react";
import { createContext, useContext, useState } from "react";

export type SearchRadius = 5 | 10 | 30 | 50 | 100;
type LocationState = {
  latitude: number | null;
  longitude: number | null;
  label: string;
  radius: SearchRadius;
  status: "fallback" | "requesting" | "allowed" | "denied" | "unsupported";
};
type LocationContextValue = LocationState & {
  requestLocation: () => void;
  setRadius: (radius: SearchRadius) => void;
};

const fallback: LocationState = {
  latitude: null,
  longitude: null,
  label: "Brisbane",
  radius: 30,
  status: "fallback",
};

function storedState(): LocationState {
  if (typeof window === "undefined") return fallback;
  try {
    const value = window.localStorage.getItem("krosspaths-location");
    return value ? { ...fallback, ...JSON.parse(value) } : fallback;
  } catch {
    return fallback;
  }
}

const LocationContext = createContext<LocationContextValue | null>(null);

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useState<LocationState>(storedState);
  const [promptOpen, setPromptOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    const forced = new URLSearchParams(window.location.search).has(
      "locationPrompt",
    );
    return (
      !window.localStorage.getItem("krosspaths-location-choice") &&
      (!navigator.webdriver || forced)
    );
  });

  function save(next: LocationState) {
    setLocation(next);
    window.localStorage.setItem("krosspaths-location", JSON.stringify(next));
  }

  function requestLocation() {
    setPromptOpen(false);
    if (!navigator.geolocation) {
      save({ ...location, status: "unsupported", label: "Brisbane" });
      window.localStorage.setItem("krosspaths-location-choice", "unsupported");
      return;
    }
    setLocation((current) => ({ ...current, status: "requesting" }));
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const next: LocationState = {
          ...location,
          latitude: coords.latitude,
          longitude: coords.longitude,
          label: "Current location",
          status: "allowed",
        };
        save(next);
        window.localStorage.setItem("krosspaths-location-choice", "allowed");
      },
      () => {
        save({
          ...location,
          latitude: null,
          longitude: null,
          label: "Brisbane",
          status: "denied",
        });
        window.localStorage.setItem("krosspaths-location-choice", "denied");
      },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 300_000 },
    );
  }

  function setRadius(radius: SearchRadius) {
    save({ ...location, radius });
  }

  function useBrisbane() {
    save({
      ...location,
      latitude: null,
      longitude: null,
      label: "Brisbane",
      status: "denied",
    });
    window.localStorage.setItem("krosspaths-location-choice", "denied");
    setPromptOpen(false);
  }

  return (
    <LocationContext.Provider
      value={{ ...location, requestLocation, setRadius }}
    >
      {children}
      {promptOpen && (
        <div className="location-prompt-backdrop">
          <section
            className="location-prompt"
            role="dialog"
            aria-modal="true"
            aria-labelledby="location-permission-heading"
          >
            <button
              className="location-prompt-close"
              onClick={useBrisbane}
              aria-label="Use Brisbane instead"
            >
              <X size={19} />
            </button>
            <span className="location-prompt-icon">
              <Navigation size={26} />
            </span>
            <h2 id="location-permission-heading">See Posts near you</h2>
            <p>
              Allow your location so Krosspaths can show Posts within your
              chosen distance. Your exact location is not shown to other
              members.
            </p>
            <button
              className="button button-primary button-block"
              onClick={requestLocation}
            >
              Allow location
            </button>
            <button
              className="button button-quiet button-block"
              onClick={useBrisbane}
            >
              Use Brisbane for now
            </button>
          </section>
        </div>
      )}
    </LocationContext.Provider>
  );
}

export function useSearchLocation() {
  const value = useContext(LocationContext);
  if (!value)
    throw new Error("useSearchLocation must be used inside LocationProvider");
  return value;
}

export function LocationControls({ compact = false }: { compact?: boolean }) {
  const location = useSearchLocation();
  return (
    <div className={`location-controls ${compact ? "compact" : ""}`}>
      <button
        className="location-selector"
        type="button"
        onClick={location.requestLocation}
        aria-label={`Search location: ${location.label}`}
      >
        <MapPin size={17} />
        <span>
          {location.status === "requesting" ? "Finding you…" : location.label}
        </span>
      </button>
      <label className="radius-selector">
        <span className="sr-only">Search radius</span>
        <select
          value={location.radius}
          onChange={(event) =>
            location.setRadius(Number(event.target.value) as SearchRadius)
          }
        >
          {[5, 10, 30, 50, 100].map((radius) => (
            <option value={radius} key={radius}>
              + {radius} km
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
